import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, ImagePlus, X, Star } from "lucide-react";
import { rotatePrompt } from "@/lib/text";
import PromptCallout from "@/components/PromptCallout";

function AuthedImage({ url, alt, className }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        const rel = url.replace(/^\/api/, "");
        const r = await api.get(rel, { responseType: "blob" });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch {}
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);
  if (!src) return <div className={`${className} bg-secondary/50 animate-pulse`} />;
  return <img src={src} alt={alt} className={className} />;
}

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState("");
  const [week, setWeek] = useState("");
  const [pendingImage, setPendingImage] = useState(null); // File
  const [promptsByWeek, setPromptsByWeek] = useState({});
  const [starred, setStarred] = useState([]);
  const fileRef = useRef(null);

  const load = async () => {
    const [{ data: items }, { data: pmap }, { data: favs }] = await Promise.all([
      api.get("/journal"),
      api.get("/journal/prompts").catch(() => ({ data: {} })),
      api.get("/prompts/favorites").catch(() => ({ data: [] })),
    ]);
    setEntries(items);
    setPromptsByWeek(pmap || {});
    setStarred(favs || []);
    // Default the week selector to the most recent entry's week, or Week 1 for first-time journalers
    if (!week) {
      const lastWithWeek = items.find((e) => e.week_number);
      setWeek(String(lastWithWeek?.week_number || 1));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activeWeek = week ? +week : 1;
  const activePrompts = promptsByWeek[String(activeWeek)] || promptsByWeek[activeWeek] || [];
  const livePrompt = rotatePrompt(activePrompts) || "In what area is God inviting you to grow this week, and what is one step you will take?";

  const unstar = async (fav) => {
    try {
      await api.delete("/prompts/favorites", {
        data: { week_number: fav.week_number, prompt: fav.prompt },
      });
      setStarred((s) => s.filter((f) => f.id !== fav.id));
      toast("Unstarred.");
    } catch {
      toast.error("Could not remove that star.");
    }
  };

  const submit = async () => {
    if (!text.trim()) return;
    try {
      const { data: entry } = await api.post("/journal", {
        entry: text, week_number: week ? +week : 0, prompt: livePrompt,
      });
      if (pendingImage) {
        const fd = new FormData();
        fd.append("file", pendingImage);
        await api.post(`/uploads/journal/${entry.id}/image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setText(""); setWeek(""); setPendingImage(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Saved.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    }
  };

  const del = async (id) => {
    await api.delete(`/journal/${id}`);
    load();
  };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)."); return; }
    setPendingImage(f);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-serif text-5xl leading-tight">Your journal</h1>
      <p className="text-muted-foreground mt-3">A quiet place to write honestly.</p>

      <section className="mt-10 border border-border bg-card p-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">New entry</div>
        {activePrompts.length > 0 && (
          <PromptCallout
            weekNumber={activeWeek}
            prompt={livePrompt}
            eyebrow={`Week ${activeWeek} · Today's prompt`}
            onToggled={async () => {
              try {
                const { data } = await api.get("/prompts/favorites");
                setStarred(data || []);
              } catch { /* best-effort refresh */ }
            }}
          />
        )}
        <Textarea data-testid="journal-text" rows={6} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={livePrompt}
          className="bg-transparent border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary" />
        {pendingImage && (
          <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground border border-dashed border-border p-3">
            <ImagePlus className="w-4 h-4 text-primary" />
            <span className="flex-1 truncate">{pendingImage.name}</span>
            <button onClick={() => { setPendingImage(null); if (fileRef.current) fileRef.current.value = ""; }}
              data-testid="clear-pending-image" className="text-muted-foreground hover:text-destructive">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            Related to week
            <input type="number" min="1" max="11" value={week} onChange={(e) => setWeek(e.target.value)}
              data-testid="journal-week" className="notebook-input w-16 ml-2" placeholder="—" />
          </label>
          <div className="flex gap-3 items-center">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="journal-image-file" />
            <Button type="button" variant="ghost" size="sm" data-testid="journal-attach-image"
              onClick={() => fileRef.current?.click()}>
              <ImagePlus className="w-4 h-4 mr-2" /> Attach photo
            </Button>
            <Button onClick={submit} data-testid="journal-save" className="rounded-full">Save entry</Button>
          </div>
        </div>
      </section>

      {starred.length > 0 && (
        <section className="mt-14" data-testid="starred-prompts-section">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
            <span>Your starred prompts</span>
            <span className="flex-1 rule-line mb-1"></span>
            <span className="text-xs text-muted-foreground">{starred.length}</span>
          </h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-xl">
            The prompts that landed for you. We'll resurface a few in your Sunday digest email.
          </p>
          <ul className="space-y-4">
            {starred.map((f) => (
              <li key={f.id} data-testid={`starred-prompt-${f.id}`}
                className="group flex items-start gap-3 border-l-2 border-primary/40 pl-4 py-1">
                <Star className="w-4 h-4 mt-1.5 text-primary shrink-0" fill="currentColor" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    Week {f.week_number}
                  </div>
                  <p className="font-serif italic leading-relaxed">{f.prompt}</p>
                </div>
                <button
                  onClick={() => unstar(f)}
                  data-testid={`starred-unstar-${f.id}`}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-muted-foreground hover:text-destructive mt-1 shrink-0"
                >
                  Unstar
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-14">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
          <span>Past reflections</span>
          <span className="flex-1 rule-line mb-1"></span>
          <span className="text-xs text-muted-foreground">{entries.length}</span>
        </h2>
        {entries.length === 0 ? (
          <p className="text-muted-foreground italic">Nothing yet. Your first honest sentence is the hardest.</p>
        ) : (
          <ul className="space-y-8">
            {entries.map((e) => (
              <li key={e.id} className="border-l border-border pl-5 group" data-testid={`journal-entry-${e.id}`}>
                <div className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>{new Date(e.created_at).toLocaleString()}{e.week_number ? ` · Week ${e.week_number}` : ""}</span>
                  <button onClick={() => del(e.id)} data-testid={`delete-${e.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap leading-relaxed">{e.entry}</p>
                {e.image_url && (
                  <AuthedImage url={e.image_url} alt="Attachment"
                    className="mt-4 max-w-md w-full h-auto border border-border" />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
